// Portals-4 (2026-06-21) — Right-side People & Access drawer for the
// Client Portal. The primary user can:
//   - see every human (primary + members + pending invites) on the active entity
//   - toggle the 4 per-feature access switches on each member
//   - revoke access (with confirm)
//   - invite a teammate by email with a starter access map
//
// Members see the same list but read-only (no edits, no invite form). The
// backend enforces the same gate, so the UI hide is purely UX (Portals-5
// will tighten the capability gates).
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Users, Mail, ShieldCheck, Trash2, Loader2, UserPlus, Send, Crown, Clock,
} from "lucide-react";
import { useConfirmDialog } from "@/lib/useConfirmDialog";  // PR #95 / UX-1

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror of backend ENTITY_ACCESS_KEYS. Keep these in lockstep with
// backend/models.py — if a key is added there, add it here too.
const ACCESS_TOGGLES = [
  { key: "view_deals",        label: "View deals",        hint: "See applications, status, and progress." },
  { key: "upload_documents",  label: "Upload documents",  hint: "Add files to checklists and doc requests." },
  { key: "sign_documents",    label: "Sign documents",    hint: "Receive and complete e-sign envelopes." },
  { key: "view_invoices",     label: "View invoices",     hint: "See and download invoices for this entity." },
];

const DEFAULT_INVITE_ACCESS = {
  view_deals: true,
  upload_documents: true,
  sign_documents: false,
  view_invoices: true,
};

function initials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function AvatarBubble({ name, email, isPrimary }) {
  return (
    <div
      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0
        ${isPrimary ? "bg-[#bd9a59] text-white" : "bg-[#021d49]/10 text-[#021d49]"}`}
    >
      {initials(name, email)}
    </div>
  );
}

export default function PeopleDrawer({ entityId, entityName, isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);       // { users, viewer_role, primary_user_id }
  const [pendingRow, setPendingRow] = useState(null);  // user_id currently being mutated
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAccess, setInviteAccess] = useState(DEFAULT_INVITE_ACCESS);
  const [sending, setSending] = useState(false);
  const { confirm, dialog } = useConfirmDialog();  // PR #95 / UX-1

  const isPrimary = data?.viewer_role === "owner";

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client-entities/${entityId}/users`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load people");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    if (isOpen && entityId) load();
    if (!isOpen) {
      setShowInvite(false);
      setInviteEmail("");
      setInviteAccess(DEFAULT_INVITE_ACCESS);
    }
  }, [isOpen, entityId, load]);

  const toggleAccess = async (userId, key, nextOn) => {
    if (!isPrimary) return;  // belt + braces — backend also enforces
    const row = data?.users?.find((u) => u.user_id === userId);
    if (!row || row.role === "primary") return;
    const nextAccess = { ...row.access, [key]: nextOn };
    setPendingRow(userId);
    try {
      await axios.patch(`${API}/client-entities/${entityId}/users/${userId}/access`, {
        access: nextAccess,
      });
      setData((prev) => prev && {
        ...prev,
        users: prev.users.map((u) => (u.user_id === userId ? { ...u, access: nextAccess } : u)),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update access");
    } finally {
      setPendingRow(null);
    }
  };

  const removeUser = async (userId, displayName) => {
    if (!isPrimary) return;
    if (!(await confirm({ title: `Remove ${displayName}?`, description: `Remove ${displayName} from ${entityName || "this company"}?`, confirmLabel: "Remove", destructive: true }))) return;
    setPendingRow(userId);
    try {
      await axios.delete(`${API}/client-entities/${entityId}/users/${userId}`);
      toast.success(`${displayName} removed`);
      setData((prev) => prev && {
        ...prev,
        users: prev.users.filter((u) => u.user_id !== userId),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove user");
    } finally {
      setPendingRow(null);
    }
  };

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Email is required");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/client-entities/${entityId}/invite`, {
        email,
        role: "viewer",
        access: inviteAccess,
      });
      toast.success(`Invite sent to ${email}`);
      setInviteEmail("");
      setInviteAccess(DEFAULT_INVITE_ACCESS);
      setShowInvite(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {dialog}
      {/* Scrim */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-white shadow-2xl z-50 flex flex-col"
        role="dialog"
        aria-label="People and access"
        data-testid="people-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-[#021d49] text-white">
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-5 h-5 text-[#bd9a59]" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold truncate">People & access</h2>
              <p className="text-[11px] text-white/60 truncate">{entityName || "Company"}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white"
            aria-label="Close people drawer"
            data-testid="people-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {!loading && (data?.users || []).map((u) => {
            const isPrimaryRow = u.role === "primary";
            const isMutating = pendingRow === u.user_id;
            return (
              <div
                key={u.user_id}
                className="border border-gray-200 rounded-lg p-3 bg-white"
                data-testid={`people-row-${u.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <AvatarBubble name={u.name} email={u.email} isPrimary={isPrimaryRow} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#021d49] truncate">{u.name}</p>
                      {isPrimaryRow && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full bg-[#bd9a59]/15 text-[#8a6f3c] font-semibold">
                          <Crown className="w-3 h-3" /> Primary
                        </span>
                      )}
                      {u.pending && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-50 text-amber-600 font-semibold">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                  </div>
                  {isPrimary && !isPrimaryRow && (
                    <button
                      onClick={() => removeUser(u.user_id, u.name)}
                      disabled={isMutating}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                      title="Remove this user"
                      data-testid={`people-remove-${u.user_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Per-feature toggles */}
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {ACCESS_TOGGLES.map((t) => {
                    const on = !!u.access?.[t.key];
                    const disabled = isPrimaryRow || !isPrimary || isMutating;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => !disabled && toggleAccess(u.user_id, t.key, !on)}
                        disabled={disabled}
                        title={t.hint + (isPrimaryRow ? " (Primary always has full access)" : "")}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-[11px] text-left border transition-colors
                          ${on
                            ? "bg-[#021d49]/5 border-[#021d49]/20 text-[#021d49]"
                            : "bg-gray-50 border-gray-200 text-gray-500"}
                          ${disabled ? "cursor-default opacity-90" : "cursor-pointer hover:border-[#bd9a59]"}`}
                        data-testid={`people-toggle-${u.user_id}-${t.key}`}
                      >
                        <ShieldCheck className={`w-3 h-3 ${on ? "text-[#bd9a59]" : "text-gray-300"}`} />
                        <span className="truncate">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {!loading && (!data?.users || data.users.length === 0) && (
            <div className="text-sm text-gray-500 text-center py-8">
              No people yet — invite a teammate to get started.
            </div>
          )}
        </div>

        {/* Footer: invite form (primary-only) */}
        {isPrimary && (
          <div className="border-t bg-gray-50 px-5 py-4">
            {!showInvite ? (
              <button
                type="button"
                onClick={() => setShowInvite(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#021d49] text-white text-sm rounded-lg hover:bg-[#052b66]"
                data-testid="people-invite-open"
              >
                <UserPlus className="w-4 h-4" />
                Invite teammate
              </button>
            ) : (
              <form onSubmit={sendInvite} className="space-y-3" data-testid="people-invite-form">
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Email</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="teammate@example.com"
                      required
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#bd9a59]"
                      data-testid="people-invite-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Starter access</label>
                  <div className="mt-1 grid grid-cols-2 gap-1.5">
                    {ACCESS_TOGGLES.map((t) => (
                      <label
                        key={t.key}
                        className="flex items-center gap-1.5 text-xs text-[#021d49] px-2 py-1.5 rounded border border-gray-200 bg-white cursor-pointer hover:border-[#bd9a59]"
                      >
                        <input
                          type="checkbox"
                          checked={!!inviteAccess[t.key]}
                          onChange={(e) =>
                            setInviteAccess((prev) => ({ ...prev, [t.key]: e.target.checked }))
                          }
                          className="accent-[#bd9a59]"
                          data-testid={`people-invite-${t.key}`}
                        />
                        <span className="truncate">{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowInvite(false); setInviteEmail(""); }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sending || !inviteEmail.trim()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#021d49] text-white text-sm rounded-lg hover:bg-[#052b66] disabled:opacity-50"
                    data-testid="people-invite-send"
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Send invite
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
